import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DOMAINS } from '../../../shared/constants/domains';
import { generateUUID } from '../../../shared/utils/uuid';
import { createInitialSRData } from '../engine/sm2';

/**
 * Add/edit flashcard form.
 */
export default function CardEditor({ cards, setCards }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const existingCard = isEditing ? cards.find(c => c.id === id) : null;

  const [form, setForm] = useState({
    domain: '3.0',
    subdomain: '',
    front: '',
    back: '',
    tags: '',
    notes: '',
  });

  useEffect(() => {
    if (existingCard) {
      setForm({
        domain: existingCard.domain,
        subdomain: existingCard.subdomain,
        front: existingCard.front,
        back: existingCard.back,
        tags: existingCard.tags.join(', '),
        notes: existingCard.notes || '',
      });
    }
  }, [existingCard]);

  // Collect unique subdomains and tags for autocomplete hints
  const existingSubdomains = [...new Set(cards.map(c => c.subdomain).filter(Boolean))];
  const existingTags = [...new Set(cards.flatMap(c => c.tags))];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.front.trim() || !form.back.trim()) return;

    const tags = form.tags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);

    if (isEditing && existingCard) {
      const updated = {
        ...existingCard,
        domain: form.domain,
        subdomain: form.subdomain.trim(),
        front: form.front.trim(),
        back: form.back.trim(),
        tags,
        notes: form.notes.trim(),
      };
      setCards(prev => prev.map(c => c.id === id ? updated : c));
    } else {
      const newCard = {
        id: generateUUID(),
        domain: form.domain,
        subdomain: form.subdomain.trim(),
        front: form.front.trim(),
        back: form.back.trim(),
        tags,
        notes: form.notes.trim(),
        source: 'user',
        created_at: new Date().toISOString(),
        sr_data: createInitialSRData(),
        stats: { times_seen: 0, times_correct: 0, streak: 0 },
      };
      setCards(prev => [...prev, newCard]);
    }

    navigate('/flashcards');
  };

  const handleDelete = () => {
    if (isEditing && window.confirm('Delete this card?')) {
      setCards(prev => prev.filter(c => c.id !== id));
      navigate('/flashcards');
    }
  };

  const inputClasses = `w-full px-4 py-2.5 rounded-xl bg-gray-800/80 border border-gray-600/50
    text-white placeholder-gray-500 transition-all duration-200
    focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none`;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 page-enter">
      <h2 className="text-xl font-bold text-white mb-6">
        {isEditing ? 'Edit Card' : 'Add New Card'}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Domain */}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1.5">Domain</label>
          <select
            value={form.domain}
            onChange={e => setForm(prev => ({ ...prev, domain: e.target.value }))}
            className={inputClasses}
          >
            {DOMAINS.map(d => (
              <option key={d.id} value={d.id}>
                {d.id} {d.name} ({Math.round(d.weight * 100)}%)
              </option>
            ))}
          </select>
        </div>

        {/* Subdomain */}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1.5">Subdomain</label>
          <input
            type="text"
            value={form.subdomain}
            onChange={e => setForm(prev => ({ ...prev, subdomain: e.target.value }))}
            placeholder="e.g., OSPF Area Types"
            list="subdomains"
            className={inputClasses}
          />
          <datalist id="subdomains">
            {existingSubdomains.map(s => <option key={s} value={s} />)}
          </datalist>
        </div>

        {/* Front */}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1.5">
            Front (Question) <span className="text-gray-600 font-normal">— supports **bold** and `code`</span>
          </label>
          <textarea
            value={form.front}
            onChange={e => setForm(prev => ({ ...prev, front: e.target.value }))}
            rows={3}
            required
            placeholder="What is the OSPF default hello timer?"
            className={`${inputClasses} resize-y`}
          />
        </div>

        {/* Back */}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1.5">
            Back (Answer)
          </label>
          <textarea
            value={form.back}
            onChange={e => setForm(prev => ({ ...prev, back: e.target.value }))}
            rows={3}
            required
            placeholder="10 seconds (dead timer = 40 seconds)"
            className={`${inputClasses} resize-y`}
          />
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1.5">Tags (comma-separated)</label>
          <input
            type="text"
            value={form.tags}
            onChange={e => setForm(prev => ({ ...prev, tags: e.target.value }))}
            placeholder="ospf, timers, infrastructure"
            className={inputClasses}
          />
          {existingTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {existingTags.slice(0, 20).map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => {
                    const current = form.tags ? form.tags.split(',').map(t => t.trim()) : [];
                    if (!current.includes(tag)) {
                      setForm(prev => ({ ...prev, tags: [...current, tag].join(', ') }));
                    }
                  }}
                  className="text-xs px-2.5 py-1 rounded-full bg-gray-700/60 text-gray-300
                    hover:bg-gray-600 hover:text-white transition-colors duration-150"
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1.5">Personal Notes (optional)</label>
          <input
            type="text"
            value={form.notes}
            onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Mnemonic or context to help remember"
            className={inputClasses}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl
              hover:from-blue-500 hover:to-blue-400 font-semibold glow-blue transition-all duration-200 active:scale-[0.98]"
          >
            {isEditing ? 'Save Changes' : 'Add Card'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/flashcards')}
            className="px-6 py-3 bg-gray-700/80 text-gray-300 rounded-xl hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={handleDelete}
              className="px-6 py-3 bg-red-900/30 text-red-400 rounded-xl hover:bg-red-900/60 transition-colors"
            >
              Delete
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
